'use client';

import { useState } from 'react';
import { Job } from '@/Types/JobTypes';
import JobCard from './JobCard';
import JobDetailPanel from './JobDetailPanel';

interface JobListingsClientProps {
  initialJobs: Job[];
}

export default function JobListingsClient({ initialJobs }: JobListingsClientProps) {
  const [selectedJob, setSelectedJob] = useState<Job | null>(initialJobs[0] || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState('');

  const filteredJobs = initialJobs.filter(job => {
    const matchesSearch =
      job.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.department?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation =
      !locationFilter || job.jobLocation.toLowerCase().includes(locationFilter.toLowerCase());

    const matchesJobType = !jobTypeFilter || job.jobType === jobTypeFilter;

    return matchesSearch && matchesLocation && matchesJobType;
  });

  const uniqueLocations = Array.from(new Set(initialJobs.map(job => job.jobLocation)));
  const uniqueJobTypes = Array.from(new Set(initialJobs.map(job => job.jobType).filter(Boolean)));

  const handleClearFilters = () => {
    setSearchTerm('');
    setLocationFilter('');
    setJobTypeFilter('');
  };

  const hasActiveFilters = searchTerm || locationFilter || jobTypeFilter;

  return (
    <div className="flex flex-col w-full">

      {/* Layout Wrapper */}
      <div className="flex flex-col md:flex-row gap-6 w-full px-4 md:px-0">

        {/* Left Side — Job List */}
        <div className="
          flex flex-col 
          w-full md:w-[380px] 
          gap-3 
          max-h-[650px] md:max-h-[800px] 
          overflow-y-auto 
          pr-1 md:pr-2 
          flex-shrink-0
        ">
          {filteredJobs.length > 0 ? (
            filteredJobs.map(job => (
              <JobCard
                key={job._id}
                job={job}
                isSelected={selectedJob?._id === job._id}
                onClick={() => setSelectedJob(job)}
              />
            ))
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-[15px]">
                No positions found matching your criteria
              </p>

              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="mt-3 text-secondary hover:underline text-[14px] font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right Side — Job Details */}
        <div className="flex-1 min-h-[400px] md:min-h-[800px] w-full">
          <JobDetailPanel job={selectedJob} />
        </div>
      </div>
    </div>
  );
}
